import { HttpClient, HttpEvent } from '@angular/common/http';
import { Injectable, NgZone, OnDestroy } from '@angular/core';
import {
  ModuleNotifications,
  ReleaseAdminMeta,
  ReleaseIgnoreSource,
  ReleaseOption,
  ReleaseUploadLimits,
  ReleaseWindowRequest,
  RemmiRelease,
  RemmiReleaseAdmin,
} from '@models/remmi-release.model';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { ApiService } from './api.service';

const REFRESH_EVERY_MS = 10 * 60 * 1000;

const DEFAULT_LIMITS: ReleaseUploadLimits = {
  imageMaxBytes: 15 * 1024 * 1024,
  videoMaxBytes: 500 * 1024 * 1024,
  maxItems: 20,
  imageMimes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
  videoMimes: ['video/mp4', 'video/webm', 'video/quicktime'],
};

/**
 * Remmi Releases: the user-side banner store plus every admin call.
 *
 * Contract: `docs/backend/remmi-releases-frontend.md`. The backend decides who sees what (status,
 * notification switch, role/permission targeting, the user's own history) — nothing here filters
 * by role or permission.
 *
 * `notifications$` holds the all-modules payload so every banner reads one request instead of
 * each page asking for its own module. After view / ignore / don't-show-again the release is
 * dropped locally first, so the banner disappears at once whatever the network does.
 */
@Injectable({ providedIn: 'root' })
export class RemmiReleasesService implements OnDestroy {
  private readonly endpoint = '/remmi-releases';
  private readonly _notifications$ = new BehaviorSubject<ModuleNotifications[]>([]);
  private readonly _windowRequest$ = new Subject<ReleaseWindowRequest>();
  private readonly _historyOpen$ = new BehaviorSubject<boolean>(false);
  private loadedForKey: string = null;
  private refreshTimer: any = null;
  private adminMeta$: Observable<ReleaseAdminMeta> = null;
  private readonly onVisible = () => {
    if (document.visibilityState === 'visible') {
      this.refreshNotifications();
    }
  };

  constructor(
    private api: ApiService,
    private http: HttpClient,
    private zone: NgZone
  ) {}

  ngOnDestroy(): void {
    this.stopAutoRefresh();
  }

  get notifications$(): Observable<ModuleNotifications[]> {
    return this._notifications$.asObservable();
  }

  get windowRequest$(): Observable<ReleaseWindowRequest> {
    return this._windowRequest$.asObservable();
  }

  get historyOpen$(): Observable<boolean> {
    return this._historyOpen$.asObservable();
  }

  forModule(modules: ModuleNotifications[], key: string): ModuleNotifications | null {
    const entry = modules.find(itm => itm.module === key);
    return entry?.count && entry.banner ? entry : null;
  }

  openWindow(request: ReleaseWindowRequest): void {
    this._windowRequest$.next(request);
  }

  openHistory(): void {
    this._historyOpen$.next(true);
  }

  closeHistory(): void {
    this._historyOpen$.next(false);
  }

  /**
   * Loads once per user + office, then keeps itself fresh (every 10 minutes and whenever the tab
   * becomes visible again). Keyed on both because this service outlives a logout and an office
   * switch — the next session must not inherit the previous one's banners.
   */
  ensureLoaded(): void {
    const key = `${this.api.getUserId()}|${this.api.getOfficeId()}`;
    if (!this.api.getUserId() || this.loadedForKey === key) {
      return;
    }
    this.loadedForKey = key;
    this._notifications$.next([]);
    this.refreshNotifications();
    this.startAutoRefresh();
  }

  refreshNotifications(): void {
    if (!this.api.getUserId()) {
      return;
    }
    this.api.get(`${this.endpoint}/notifications`).subscribe({
      next: (res: any) => this._notifications$.next(this.modulesFrom(res)),
      // A failed refresh keeps whatever is already showing rather than blanking every banner.
      error: () => {},
    });
  }

  // ---- User endpoints ----

  getRelease(id: string): Observable<RemmiRelease> {
    return this.api.get(`${this.endpoint}/${id}`).pipe(map((res: any) => res?.data));
  }

  /** Window opened. Called on every open — the backend counts views. */
  view(id: string): Observable<any> {
    this.removeLocal([id]);
    return this.api.post(`${this.endpoint}/${id}/view`, {});
  }

  /** Finish pressed on the last section. Never call this for Close, ×, Esc or backdrop. */
  complete(id: string): Observable<any> {
    return this.api.post(`${this.endpoint}/${id}/complete`, {});
  }

  dontShowAgain(id: string): Observable<any> {
    this.removeLocal([id]);
    return this.api.post(`${this.endpoint}/${id}/dont-show-again`, {});
  }

  ignore(id: string, source: ReleaseIgnoreSource): Observable<any> {
    this.removeLocal([id]);
    return this.api.post(`${this.endpoint}/${id}/ignore`, { source });
  }

  ignoreMany(ids: string[], source: ReleaseIgnoreSource): Observable<any> {
    this.removeLocal(ids);
    return this.api.post(`${this.endpoint}/ignore`, { release_ids: ids, source });
  }

  history(params: { module?: string; type?: string; search?: string; page?: number; limit?: number }): Observable<any> {
    return this.api.get(`${this.endpoint}/history`, this.cleanParams(params));
  }

  // ---- Admin endpoints (Master Admin only) ----

  /** Dropdown data rarely changes within a session, so it is fetched once and shared. */
  adminMeta(): Observable<ReleaseAdminMeta> {
    if (!this.adminMeta$) {
      this.adminMeta$ = this.api.get(`${this.endpoint}/admin/meta`).pipe(
        map((res: any) => this.normaliseMeta(res?.data)),
        catchError(err => {
          this.adminMeta$ = null;
          throw err;
        }),
        shareReplay(1)
      );
    }
    return this.adminMeta$;
  }

  adminList(params: any): Observable<any> {
    return this.api.get(`${this.endpoint}/admin`, this.cleanParams(params));
  }

  adminGet(id: string): Observable<RemmiReleaseAdmin> {
    return this.api.get(`${this.endpoint}/admin/${id}`).pipe(map((res: any) => res?.data));
  }

  adminCreate(body: any): Observable<RemmiReleaseAdmin> {
    return this.api.post(`${this.endpoint}/admin`, body).pipe(map((res: any) => res?.data));
  }

  /** Partial edit. Never re-notifies users — only {@link adminReannounce} does. */
  adminUpdate(id: string, body: any): Observable<RemmiReleaseAdmin> {
    return this.api.put(`${this.endpoint}/admin/${id}`, body).pipe(map((res: any) => res?.data));
  }

  adminDelete(id: string): Observable<any> {
    return this.api.delete(`${this.endpoint}/admin/${id}`);
  }

  adminPublish(id: string, notificationEnabled: boolean): Observable<RemmiReleaseAdmin> {
    return this.api.post(`${this.endpoint}/admin/${id}/publish`, { notification_enabled: notificationEnabled }).pipe(map((res: any) => res?.data));
  }

  adminArchive(id: string): Observable<RemmiReleaseAdmin> {
    return this.api.post(`${this.endpoint}/admin/${id}/archive`, {}).pipe(map((res: any) => res?.data));
  }

  adminUnarchive(id: string): Observable<RemmiReleaseAdmin> {
    return this.api.post(`${this.endpoint}/admin/${id}/unarchive`, {}).pipe(map((res: any) => res?.data));
  }

  adminSetNotification(id: string, enabled: boolean): Observable<RemmiReleaseAdmin> {
    return this.api.put(`${this.endpoint}/admin/${id}/notification`, { enabled }).pipe(map((res: any) => res?.data));
  }

  adminReannounce(id: string): Observable<RemmiReleaseAdmin> {
    return this.api.post(`${this.endpoint}/admin/${id}/reannounce`, {}).pipe(map((res: any) => res?.data));
  }

  /**
   * Multipart upload with progress events. Goes through HttpClient directly because
   * `ApiService.post` forces a JSON Content-Type, and the browser must set the multipart boundary.
   * The interceptor still attaches the Bearer token.
   */
  adminUploadMedia(id: string, file: File, caption = ''): Observable<HttpEvent<any>> {
    return this.http.post(`${this.api.baseUrl}${this.endpoint}/admin/${id}/media`, this.mediaForm(file, caption), {
      reportProgress: true,
      observe: 'events',
    });
  }

  adminReplaceMedia(id: string, mediaId: string, file: File, caption = ''): Observable<HttpEvent<any>> {
    return this.http.post(`${this.api.baseUrl}${this.endpoint}/admin/${id}/media/${mediaId}/replace`, this.mediaForm(file, caption), {
      reportProgress: true,
      observe: 'events',
    });
  }

  /** `media_ids` must list every media id on the release, in the new order — a partial list is a 409. */
  adminReorderMedia(id: string, mediaIds: string[]): Observable<RemmiReleaseAdmin> {
    return this.api.put(`${this.endpoint}/admin/${id}/media/order`, { media_ids: mediaIds }).pipe(map((res: any) => res?.data));
  }

  adminUpdateMediaCaption(id: string, mediaId: string, caption: string): Observable<RemmiReleaseAdmin> {
    return this.api.put(`${this.endpoint}/admin/${id}/media/${mediaId}`, { caption }).pipe(map((res: any) => res?.data));
  }

  adminDeleteMedia(id: string, mediaId: string): Observable<RemmiReleaseAdmin> {
    return this.api.delete(`${this.endpoint}/admin/${id}/media/${mediaId}`).pipe(map((res: any) => res?.data));
  }

  adminEngagement(params: any, releaseId?: string): Observable<any> {
    const path = releaseId ? `${this.endpoint}/admin/${releaseId}/engagement` : `${this.endpoint}/admin/engagement`;
    return this.api.get(path, this.cleanParams(params));
  }

  adminEngagementFilters(): Observable<{ users: ReleaseOption[]; offices: ReleaseOption[]; releases: ReleaseOption[]; actions: ReleaseOption[] }> {
    return this.api.get(`${this.endpoint}/admin/engagement/filters`).pipe(
      map((res: any) => ({
        users: this.toOptions(res?.data?.users),
        offices: this.toOptions(res?.data?.offices),
        releases: this.toOptions(res?.data?.releases),
        actions: this.toOptions(res?.data?.actions),
      })),
      catchError(() => of({ users: [], offices: [], releases: [], actions: [] }))
    );
  }

  /**
   * Rows out of a list response. The guide doesn't show the list body, so the common shapes are all
   * accepted: `data` as the array, or the array under `data.items` / `data.releases` / `data.docs` /
   * `data.rows` / `data.data`. See `docs/backend/remmi-releases-open-questions.md` row 4.
   */
  rowsFrom(res: any): any[] {
    const data = res?.data;
    if (Array.isArray(data)) {
      return data;
    }
    return data?.items ?? data?.releases ?? data?.docs ?? data?.rows ?? data?.data ?? [];
  }

  /** List total wherever the backend puts it, falling back to the rows received. */
  totalFrom(res: any, rowCount: number): number {
    return res?.meta?.total ?? res?.data?.total ?? res?.meta?.pagination?.total ?? res?.data?.pagination?.total ?? res?.data?.meta?.total ?? rowCount;
  }

  /** Readable error text: ApiService rejects writes with the response body, reads with the HttpErrorResponse. */
  errorMessage(err: any, fallback: string): string {
    return err?.error?.message || err?.message || fallback;
  }

  // ---- Internals ----

  /**
   * Per-module entries from `GET /notifications`. The guide documents `data.modules`, but a response
   * that is the module array itself, or a single-module object, is accepted too, so a shape mismatch
   * can't silently hide every banner. Each entry's count and banner are rebuilt from its releases when
   * the backend leaves them out.
   */
  private modulesFrom(res: any): ModuleNotifications[] {
    const data = res?.data;
    let modules: any[] = [];
    if (Array.isArray(data?.modules)) {
      modules = data.modules;
    } else if (Array.isArray(data)) {
      modules = data;
    } else if (data?.module) {
      modules = [data];
    }
    return modules
      .filter(entry => entry?.module)
      .map(entry => {
        const releases = entry.releases ?? [];
        const count = entry.count ?? releases.length;
        return { ...entry, releases, count, banner: entry.banner ?? this.bannerFor(releases) };
      });
  }

  /**
   * Drops releases from the local store and rebuilds each touched module's banner the same way the
   * backend would: one pending release → single banner with its own text, several → one group banner.
   */
  private removeLocal(ids: string[]): void {
    const drop = new Set(ids);
    const next = this._notifications$.value
      .map(entry => {
        const releases = (entry.releases ?? []).filter(release => !drop.has(release._id));
        if (releases.length === (entry.releases ?? []).length) {
          return entry;
        }
        return { ...entry, releases, count: releases.length, banner: this.bannerFor(releases, entry.banner?.label) };
      })
      .filter(entry => entry.count > 0);
    this._notifications$.next(next);
  }

  private bannerFor(releases: RemmiRelease[], label = 'Feature'): ModuleNotifications['banner'] {
    if (!releases.length) {
      return null;
    }
    if (releases.length === 1) {
      return { mode: 'single', label, text: releases[0].banner_text || releases[0].summary || releases[0].title, release_id: releases[0]._id };
    }
    return { mode: 'group', label, text: `${releases.length} new features available`, release_ids: releases.map(release => release._id) };
  }

  private startAutoRefresh(): void {
    if (this.refreshTimer) {
      return;
    }
    // Outside Angular so the 10-minute timer doesn't keep the zone (and e2e stability checks) busy.
    this.zone.runOutsideAngular(() => {
      this.refreshTimer = setInterval(() => this.zone.run(() => this.refreshNotifications()), REFRESH_EVERY_MS);
    });
    document.addEventListener('visibilitychange', this.onVisible);
  }

  private stopAutoRefresh(): void {
    clearInterval(this.refreshTimer);
    this.refreshTimer = null;
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  private mediaForm(file: File, caption: string): FormData {
    const form = new FormData();
    form.append('file', file);
    form.append('caption', caption ?? '');
    return form;
  }

  /** `ApiService.get` sets every key it is given, so empty filters are left out rather than sent as "". */
  private cleanParams(params: any): any {
    const clean: any = {};
    Object.keys(params ?? {}).forEach(key => {
      const value = params[key];
      if (value === null || value === undefined || value === '' || (Array.isArray(value) && !value.length)) {
        return;
      }
      clean[key] = Array.isArray(value) ? value.join(',') : String(value);
    });
    return clean;
  }

  /**
   * The guide names the meta lists but not each item's exact shape, so items are accepted as plain
   * strings or as objects keyed `value`/`key`/`_id`/`name` — whichever the backend sends.
   */
  private normaliseMeta(data: any): ReleaseAdminMeta {
    return {
      modules: this.toOptions(data?.modules),
      types: this.toOptions(data?.types),
      statuses: this.toOptions(data?.statuses),
      actions: this.toOptions(data?.actions),
      roles: this.roleOptions(data?.roles),
      // Permissions are matched on `name`, labelled by `label`.
      permissions: (data?.permissions ?? []).map((itm: any) =>
        typeof itm === 'string' ? { value: itm, label: itm } : { value: itm?.name ?? itm?.value, label: itm?.label ?? itm?.name }
      ),
      limits: this.normaliseLimits(data?.limits),
    };
  }

  /** Backend sends `{ image_max_mb, video_max_mb, media_max }` (remmiRelease.service#getMeta). */
  private normaliseLimits(limits: any): ReleaseUploadLimits {
    const mb = (value: number) => (value ? value * 1024 * 1024 : null);
    return {
      imageMaxBytes: mb(limits?.image_max_mb) ?? DEFAULT_LIMITS.imageMaxBytes,
      videoMaxBytes: mb(limits?.video_max_mb) ?? DEFAULT_LIMITS.videoMaxBytes,
      maxItems: limits?.media_max ?? DEFAULT_LIMITS.maxItems,
      imageMimes: limits?.image?.mimes ?? limits?.image_mimes ?? DEFAULT_LIMITS.imageMimes,
      videoMimes: limits?.video?.mimes ?? limits?.video_mimes ?? DEFAULT_LIMITS.videoMimes,
    };
  }

  /**
   * Roles come back from every office (`{ _id, name, role_name, office_id }`), and an office can keep
   * its own copy of a standard role under the same name. Targeting matches on the exact role id, so
   * office copies are labelled as such and listed after the shared roles — otherwise two "Sales Agent"
   * entries look identical and picking the wrong one hides the release from the real Sales Agents.
   */
  private roleOptions(list: any[]): ReleaseOption[] {
    const roles = (list ?? []).filter((role: any) => role?._id);
    const shared = roles.filter((role: any) => !role.office_id);
    const officeOnly = roles.filter((role: any) => role.office_id);
    return [
      ...shared.map((role: any) => ({ value: String(role._id), label: role.name })),
      ...officeOnly.map((role: any) => ({ value: String(role._id), label: `${role.name} (office role)` })),
    ];
  }

  private toOptions(list: any[]): ReleaseOption[] {
    return (list ?? []).map((itm: any) => {
      if (typeof itm === 'string') {
        return { value: itm, label: itm.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase()) };
      }
      return {
        value: itm?.value ?? itm?.key ?? itm?._id ?? itm?.id ?? itm?.name,
        label: itm?.label ?? itm?.name ?? itm?.title ?? itm?.full_name ?? String(itm?.value ?? ''),
      };
    });
  }
}
